// src/hooks/useGoftino.ts
import { useEffect } from 'react';
import TokenManager from '../utils/tokenManager';

/**
 * useGoftino
 *
 * Pushes the logged-in user's details into the Goftino chat session.
 *
 * The script itself is loaded in index.html (inline snippet in <head>) so it
 * starts downloading before React parses.
 *
 * WHY THIS HOOK NO LONGER CONFIGURES THE WIDGET
 * ---------------------------------------------
 * It used to call Goftino.setWidget({ hasIcon, iconUrl, cssUrl, margins })
 * from a `goftino_ready` listener. That never worked reliably, and the reason
 * is in the vendor code:
 *
 *   setWidget: function(d) { window.goftino_widgetdata = d; }
 *
 * That is the whole implementation — a plain assignment. The widget's
 * client.js (running inside the #goftino_w iframe) reads
 * `parent.goftino_widgetdata` exactly once, in the same synchronous statement
 * that dispatches `goftino_ready`, and never looks at hasIcon/iconUrl/cssUrl
 * again. So:
 *
 *   - Calling setWidget from a `goftino_ready` listener only wins if this
 *     hook's effect already ran and attached the listener. Every layout here
 *     sits behind a lazy() route, so on a cold chunk load the widget boots
 *     first, the event is missed, and the fallback `if (window.Goftino)`
 *     branch assigns goftino_widgetdata after boot — too late. That race is
 *     why the icon appeared "sometimes".
 *   - Calling setWidget on a route change is always a no-op: the widget has
 *     long since booted.
 *
 * The config therefore lives in index.html, assigned before the script tag is
 * injected — the only point guaranteed to be ahead of the iframe's read. That
 * is also where the desktop/mobile rule lives: bubble on desktop, hasIcon:false
 * on mobile (where chat is opened only by our own buttons calling
 * window.Goftino.toggle()). It cannot be decided per-route or per-component,
 * because by the time a route renders the widget has already booted.
 *
 * setUser is different — it is applied live by the iframe, so it stays here.
 */
declare global {
  interface Window {
    Goftino?: {
      setWidget: (config: any) => void;
      setUser: (user: any) => void;
      toggle: () => void;
    };
    goftino_widgetdata?: Record<string, any>;
  }
}

export default function useGoftino() {
  useEffect(() => {
    // Fires once per page load, after the widget iframe has booted.
    window.addEventListener('goftino_ready', updateInfoUser);

    // If the widget booted before this effect ran (fast/cached load, or a
    // lazy route that mounted late), the event is already gone — push now.
    if (window.Goftino) {
      updateInfoUser();
    }

    return () => {
      window.removeEventListener('goftino_ready', updateInfoUser);
    };
  }, []);
}

/**
 * Push current user info to Goftino. Safe to call any time — no-op if
 * Goftino isn't loaded yet or there's no logged-in user.
 *
 * Exported so other code (e.g., post-login flow) can call it to refresh
 * the chat session with new user data.
 */
export const updateInfoUser = () => {
  const { Email, Mobile, FirstName, LastName, UUID } =
    TokenManager.parsed()?.accessToken || {};

  if (!FirstName) return;

  window.Goftino?.setUser({
    name: `${FirstName} ${LastName}`,
    phone: Mobile,
    email: Email,
    about: UUID,
    forceUpdate: true
  });
};
